/**
 * The levy, as a thing a player can look at.
 *
 * Nothing here is new machinery and that is the point. Provinces have carried a
 * `levy` since the realm did; `doIncome` banks `levy × estateLevyScale` into
 * `nation.manpower` every month; `tryRaiseArmy` spends the bank. All of it has
 * worked, in full, invisibly — a player could see one number ("Levies: 340
 * banked · +58 a month") and had no way at all to ask *where the fifty-eight
 * comes from*, *what this province owes*, or *what raising a host costs them
 * next year*. That is not a simulation problem, it is a reading problem, and
 * this file is the reader.
 *
 * Two rules it keeps, both of them about not becoming a second authority:
 *
 *   • **The number is never re-derived.** Every man a month quoted here comes
 *     out of `holdingIncome(...).levy` and `estateLevyScale`, which are the two
 *     functions `doIncome` itself calls. The factors below explain that number;
 *     they never compute it. This is the failure `holdingIncome`'s own comment
 *     warns about — a panel and a month that each have their own arithmetic —
 *     and the way to not have it is to have one arithmetic.
 *   • **It writes nothing.** No new state, nothing to hash, nothing to save.
 *     A screen over a simulation that needs nothing is exactly what the item
 *     asked for.
 */

import { provinceDef } from '../../data/provinces.ts';
import { campYieldScale } from './bandits.ts';
import { Estate, estateLevyScale, HOSTS_STRIKE, standing } from './estates.ts';
import { holdingIncome, tierLevy, tierOf, tierName } from './holdings.ts';
import { armyModels, meanThreat, nationFactionId, raisePrice } from './armies.ts';
import { MAX_ARMY_POINTS, MIN_ARMY_POINTS, type CampaignState } from './types.ts';

/**
 * Why a province sends fewer men than the ground could carry.
 *
 * One word each, in the order they are worth saying. `None` is the ordinary
 * case: a settled province at its tier is not "dragged" by anything and must
 * not be drawn as though it were.
 */
export const LevyDrag = {
  None: 0,
  /** Under siege. The countryside sends almost nothing while the tents are up. */
  Siege: 1,
  /** Freshly taken, or badly run. */
  Unrest: 2,
  /** Brigands sitting on the roads out of it. */
  Brigands: 3,
  /** The holding has never been built up past what it was born as. */
  Untiered: 4,
  /** The ground itself, or a capital that has not been built. */
  Undeveloped: 5,
} as const;
export type LevyDragKind = (typeof LevyDrag)[keyof typeof LevyDrag];

export const LEVY_DRAG_WORDS: Record<number, string> = {
  [LevyDrag.None]: '',
  [LevyDrag.Siege]: 'besieged',
  [LevyDrag.Unrest]: 'restive',
  [LevyDrag.Brigands]: 'brigands',
  [LevyDrag.Untiered]: 'unbuilt',
  [LevyDrag.Undeveloped]: 'poor ground',
};

/** What one province owes its crown in men this month, and why it is that much. */
export interface LevySource {
  province: number;
  /** Men a month, exactly as `doIncome` will count them — before the estate's cut. */
  levy: number;
  /**
   * What the same ground would send with no siege, no unrest, no brigands, at
   * the tier it stands at.
   *
   * The *counterfactual a player can act on*, which is why it holds the tier
   * fixed rather than quoting the top of the ladder. "This province could send
   * eleven and sends four" is a sentence about a rebellion you can put down;
   * "could send thirty at tier ten" is a sentence about money you do not have.
   */
  settled: number;
  /** `levy / settled`, clamped to 0..1. 1 means nothing is wrong with it. */
  health: number;
  drag: LevyDragKind;
  tier: number;
  tierWord: string;
}

/** Everything the levy screen is about, in one read. */
export interface LevyReport {
  nation: number;
  /** Men in the bank right now. `nation.manpower`. */
  banked: number;
  /** What the ground sends, before the Hosts take their view of you. */
  gross: number;
  /** What actually reaches the bank: `gross × estateLevyScale`. */
  monthly: number;
  /** The Hosts' multiplier, 0 on a strike. */
  estateScale: number;
  /** The Hosts' standing, −100…100, and whether they have downed tools. */
  hostsStanding: number;
  onStrike: boolean;
  /** Men already under arms in this crown's hosts. */
  underArms: number;
  /** Province rows, richest first, then by index so the order is stable. */
  sources: LevySource[];
}

/**
 * Every province's contribution, and what the crown actually banks.
 *
 * Ordered richest first because the question this screen answers most often is
 * "where do my men come from", and the answer is a list whose top three lines
 * are usually most of it.
 */
export function levyReport(c: CampaignState, nation: number): LevyReport {
  const n = c.nations[nation];
  const scale = estateLevyScale(c, nation);
  const sources: LevySource[] = [];
  let gross = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.owner !== nation) continue;
    // The month's own reader. Never a second copy of the arithmetic.
    const levy = holdingIncome(c, p).levy;
    gross += levy;
    const tier = tierOf(c, p);
    /**
     * The same product with the three temporary terms set to their healthy
     * value. `development` stays in it: a province's development is what the
     * ground and the city on it amount to, not something that has gone wrong
     * this month.
     */
    const settled = provinceDef(p).levy * prov.development * tierLevy(tier);
    sources.push({
      province: p,
      levy,
      settled,
      health: settled > 0 ? Math.max(0, Math.min(1, levy / settled)) : 1,
      drag: dragOn(c, p),
      tier,
      tierWord: tierName(tier),
    });
  }
  sources.sort((a, b) => b.levy - a.levy || a.province - b.province);
  let underArms = 0;
  for (const a of c.armies) if (a.owner === nation) underArms += armyModels(a);
  const hosts = standing(c, nation, Estate.Hosts);
  return {
    nation,
    banked: n?.manpower ?? 0,
    gross,
    monthly: gross * scale,
    estateScale: scale,
    hostsStanding: hosts,
    onStrike: hosts <= HOSTS_STRIKE,
    underArms,
    sources,
  };
}

/**
 * The largest single reason this province is not sending what it could.
 *
 * Largest by the multiplier it costs, not by a hand-written priority: a
 * province with a besieging army outside is besieged even if it is also
 * restive, because 0.3 beats 0.8, and the sentence a player needs is the one
 * about the tents.
 */
function dragOn(c: CampaignState, p: number): LevyDragKind {
  const prov = c.provinces[p];
  const terms: Array<[LevyDragKind, number]> = [
    [LevyDrag.Siege, prov.siegeBy >= 0 ? 0.3 : 1],
    [LevyDrag.Unrest, 1 - prov.unrest],
    [LevyDrag.Brigands, campYieldScale(c, p)],
    [LevyDrag.Untiered, Math.min(1, tierLevy(tierOf(c, p)))],
    [LevyDrag.Undeveloped, Math.min(1, prov.development)],
  ];
  let worst: LevyDragKind = LevyDrag.None;
  let worstAt = 0.97;
  for (const [kind, factor] of terms) {
    if (factor < worstAt) {
      worstAt = factor;
      worst = kind;
    }
  }
  return worst;
}

/** What a muster of this size takes out of the realm, in the terms it takes it. */
export interface MusterDrain {
  points: number;
  /** Men off the bank. The same conversion `maxAffordablePoints` solves with. */
  models: number;
  gold: number;
  iron: number;
  /** Men left in the bank after it. Negative is refused, not borrowed. */
  banked: number;
  /**
   * Months of levy that muster costs — *the number the item asked for*.
   *
   * "What raising a host costs you next year" is not a gold figure, it is this:
   * at the rate the countryside sends men, how long the realm is paying for
   * the decision. Infinity when the levy is zero, which happens on a Hosts
   * strike and is a fact worth showing rather than a division to hide.
   */
  months: number;
  affordable: boolean;
}

/**
 * Price a muster in men and in months.
 *
 * `points` is clamped to the same window `tryRaiseArmy` clamps it to, so a
 * quote can never be for a host the command would not raise.
 */
export function musterDrain(c: CampaignState, nation: number, points: number): MusterDrain {
  const want = Math.max(MIN_ARMY_POINTS, Math.min(MAX_ARMY_POINTS, Math.round(points)));
  const n = c.nations[nation];
  const perModel = meanThreat(nationFactionId(c, nation));
  const models = perModel > 0 ? Math.round(want / perModel) : 0;
  const price = raisePrice(c, nation, want);
  const rate = levyRate(c, nation);
  return {
    points: want,
    models,
    gold: price.gold,
    iron: price.iron,
    banked: (n?.manpower ?? 0) - models,
    months: rate > 0 ? models / rate : Infinity,
    affordable: (n?.manpower ?? 0) >= models && (n?.treasury.gold ?? 0) >= price.gold,
  };
}

/**
 * Men a month into the bank, after the Hosts' cut.
 *
 * Its own function because three callers want it and one of them (`musterDrain`)
 * must not pay for the whole province list to get it.
 */
export function levyRate(c: CampaignState, nation: number): number {
  let gross = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    gross += holdingIncome(c, p).levy;
  }
  return gross * estateLevyScale(c, nation);
}

/**
 * Months until the bank could pay for a host of `points`, or 0 if it already
 * can. Infinity when nothing is coming in.
 */
export function monthsUntilMuster(c: CampaignState, nation: number, points: number): number {
  const drain = musterDrain(c, nation, points);
  if (drain.banked >= 0) return 0;
  const rate = levyRate(c, nation);
  return rate > 0 ? -drain.banked / rate : Infinity;
}
